import { useMemo } from 'react'
import { cn } from '@/components/ui/cn'
import type { MatchingPair } from '@/types/test'

interface MatchingQuestionProps {
  pairs: MatchingPair[]
  matches: Record<string, string>
  onMatch: (word: string, translation: string) => void
}

export function MatchingQuestion({ pairs, matches, onMatch }: MatchingQuestionProps) {
  const words = useMemo(() => pairs.map((pair) => pair.word), [pairs])
  const translations = useMemo(() => {
    const unique = new Set(pairs.map((pair) => pair.translation))
    return Array.from(unique)
  }, [pairs])

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold leading-snug text-foreground sm:text-2xl">
        Match each item to its pair
      </h2>
      <div className="space-y-3">
        {words.map((word) => (
          <div
            key={word}
            className="grid gap-3 rounded-lg border border-border/70 bg-card/50 p-3 md:grid-cols-[1fr_1.2fr] md:items-center"
          >
            <span className="font-medium text-foreground">{word}</span>
            <select
              value={matches[word] ?? ''}
              onChange={(event) => onMatch(word, event.target.value)}
              className={cn(
                'rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground',
                matches[word] ? 'border-primary ring-1 ring-primary/20' : ''
              )}
            >
              <option value="">Select answer</option>
              {translations.map((translation) => (
                <option key={`${word}-${translation}`} value={translation}>
                  {translation}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  )
}
